export const FIXED_BAR_WIDTH_PX = 16;
export const FIXED_BAR_GAP_PX = 12;
export const FIXED_SINGLE_CATEGORY_GAP_PX = 72;
export const FIXED_GROUP_GAP_PX = 48;

/**
 * Series (dodge) band-scale `paddingInner` that makes fixed-width bars sit exactly
 * `FIXED_BAR_GAP_PX` apart inside a category.
 *
 * `dodgeX({ padding })` only sets the series scale's `paddingInner`; its `paddingOuter`
 * stays at d3's default `0`. For a band scale of N series over a bandwidth `B`:
 *   step          = B / (N - paddingInner)
 *   subBandwidth  = step * (1 - paddingInner)
 * Bars are pinned to `FIXED_BAR_WIDTH_PX` (min == max width) and centred in each sub-band,
 * so the gap between adjacent bars is `step - FIXED_BAR_WIDTH_PX`. Choosing
 * `paddingInner = gap / (barWidth + gap)` yields `step = barWidth + gap` and
 * `subBandwidth = barWidth`, i.e. a `FIXED_BAR_GAP_PX` gap with zero leftover edge padding —
 * but only when the category bandwidth equals `categoryWidthPx` (see `fixedPlotWidthPx`).
 */
export const FIXED_DODGE_PADDING_INNER = FIXED_BAR_GAP_PX / (FIXED_BAR_WIDTH_PX + FIXED_BAR_GAP_PX);

export type FixedBarGeometry = {
    barsPerCategory: number;
    barWidthPx: number;
    intraBarGapPx: number;
    categoryGapPx: number;
    categoryWidthPx: number;
    categoryStridePx: number;
    xScalePaddingRatio: number;
    dodgeSeriesPaddingInner: number;
    visibleCategoryCount: number;
};

export function computeFixedBarGeometry(args: {
    barsPerCategory: number;
    availablePlotWidthPx: number;
    fallbackVisibleCategoryCount?: number;
}): FixedBarGeometry {
    const barsPerCategory = Math.max(1, Math.floor(args.barsPerCategory));
    const categoryGapPx = barsPerCategory > 1 ? FIXED_GROUP_GAP_PX : FIXED_SINGLE_CATEGORY_GAP_PX;
    const categoryWidthPx =
        barsPerCategory * FIXED_BAR_WIDTH_PX + Math.max(0, barsPerCategory - 1) * FIXED_BAR_GAP_PX;
    const categoryStridePx = categoryWidthPx + categoryGapPx;
    const xScalePaddingRatio = categoryGapPx / categoryStridePx;
    const dodgeSeriesPaddingInner = barsPerCategory > 1 ? FIXED_DODGE_PADDING_INNER : 0;
    const fallbackVisibleCategoryCount = Math.max(1, Math.floor(args.fallbackVisibleCategoryCount ?? 1));

    /**
     * A d3 band scale lays `n` categories out as `step = plotWidth / (n + xScalePaddingRatio)`
     * (paddingInner === paddingOuter === xScalePaddingRatio). For the rendered `step` to equal
     * `categoryStridePx` the plot must be `categoryStridePx * (n + xScalePaddingRatio)` wide, so
     * the largest `n` that fits the available plot is `floor(width / stride - paddingRatio)`.
     * Subtracting the padding ratio here guarantees `fixedPlotWidthPx(...)` never exceeds the
     * available width (i.e. the fixed-width chart never overflows its container).
     */
    const visibleCategoryCount =
        args.availablePlotWidthPx > 0
            ? Math.max(1, Math.floor(args.availablePlotWidthPx / categoryStridePx - xScalePaddingRatio))
            : fallbackVisibleCategoryCount;

    return {
        barsPerCategory,
        barWidthPx: FIXED_BAR_WIDTH_PX,
        intraBarGapPx: FIXED_BAR_GAP_PX,
        categoryGapPx,
        categoryWidthPx,
        categoryStridePx,
        xScalePaddingRatio,
        dodgeSeriesPaddingInner,
        visibleCategoryCount,
    };
}

/**
 * Exact plot width (px) needed to render `renderedCategoryCount` categories so that the x band
 * `step` equals `categoryStridePx` — which in turn makes every inter-category gap exactly
 * `categoryGapPx` and (with `dodgeSeriesPaddingInner`) every intra-group gap exactly
 * `FIXED_BAR_GAP_PX`, independent of the container width.
 */
export function computeFixedPlotWidthPx(geometry: FixedBarGeometry, renderedCategoryCount: number): number {
    const count = Math.max(1, Math.floor(renderedCategoryCount));
    return geometry.categoryStridePx * (count + geometry.xScalePaddingRatio);
}
